import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const parseDecodedToken = (decoded) => {
    return {
      id: decoded.userId,
      email: decoded.email,
      name: decoded.name,
      role: decoded.UserRole,
      permissions: decoded.permissions || {}, // Fallback for reloads (assume JWT has them)
    };
  };

  // Only check existing token on initial load (no [token] dep to avoid overwriting on login)
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          logout();
        } else {
          // On reload, use JWT data (no API perms available; ensure backend embeds them in JWT)
          const userData = parseDecodedToken(decoded);
          setUser(userData);
        }
      } catch {
        logout();
      }
    }
    setLoading(false);
  }, []); // Empty dep array: runs once on mount

  const login = (jwtToken, userPermissions) => {
    try {
      const decoded = jwtDecode(jwtToken);
      if (decoded.exp * 1000 < Date.now()) {
        throw new Error("Token expired");
      }

      // Now safe: store and set
      localStorage.setItem("token", jwtToken);
      setToken(jwtToken);

      const userData = parseDecodedToken(decoded);
      // Prefer API permissions (fresh)
      userData.permissions = userPermissions || userData.permissions;

      setUser(userData);
    } catch (error) {
      console.error("Invalid token:", error);
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      // Optionally navigate to /login here, or let caller handle
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    navigate("/login", { replace: true });
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
